import './App.css';
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';

import PorchpairPage from './pages/PorchpairPage';
import TrekkoraPage from './pages/TrekkoraPage';
import SeoulJuicePage from './pages/SeoulJuicePage';
import LyftPage from './pages/LyftPage';
import ResumePage from './pages/ResumePage';

import PorchpairIcon from './images/PorchpairIcon.png';
import TrekkoraIcon from './images/TrekkoraIcon.png';
import SeoulJuiceIcon from './images/SeoulJuiceIcon.png';
import LyftIcon from './images/LyftIcon.png';
import Avatar from './images/MarquesManta_Animated.png';


const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400;1,500&family=DM+Sans:wght@300;400;500&display=swap');

  html { scroll-behavior: smooth; }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink:       #1A1814;
    --ink-2:     #4A4740;
    --ink-3:     #8A8780;
    --rule:      rgba(26,24,20,0.10);
    --rule-md:   rgba(26,24,20,0.18);
    --sand:      #F7F4EE;
    --white:     #FFFFFF;
    --amber:     #B8721A;
    --amber-lt:  #FDF3E3;
    --serif:     'Cormorant Garamond', Georgia, serif;
    --sans:      'DM Sans', system-ui, sans-serif;
  }

  body {
    font-family: var(--sans);
    background: var(--white);
    color: var(--ink);
    -webkit-font-smoothing: antialiased;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(22px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes slideInRight {
    from { opacity: 0; transform: translateX(18px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .anim-fade-up     { opacity: 0; animation: fadeUp 0.7s cubic-bezier(0.22,1,0.36,1) forwards; }
  .anim-fade-in     { opacity: 0; animation: fadeIn 0.6s ease forwards; }
  .anim-slide-right { opacity: 0; animation: slideInRight 0.6s cubic-bezier(0.22,1,0.36,1) forwards; }

  .delay-1 { animation-delay: 0.08s; }
  .delay-2 { animation-delay: 0.18s; }
  .delay-3 { animation-delay: 0.30s; }
  .delay-4 { animation-delay: 0.44s; }
  .delay-5 { animation-delay: 0.58s; }

  /* ── NAV ──────────────────────────────────────────────────────────────────── */
  .mm-nav {
    position: sticky;
    top: 0;
    z-index: 100;
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(12px);
    border-bottom: 0.5px solid var(--rule);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 48px;
    height: 60px;
  }

  .mm-nav-logo {
    display: inline-flex;
    align-items: center;
    gap: 11px;
    font-family: var(--serif);
    font-size: 19px;
    font-weight: 500;
    color: var(--ink);
    text-decoration: none;
    letter-spacing: 0.01em;
    transition: transform 0.25s cubic-bezier(0.22,1,0.36,1);
  }

  .mm-nav-logo:hover { transform: scale(1.04); }

  .mm-nav-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    border: 1.5px solid var(--rule-md);
    flex-shrink: 0;
  }

  .mm-nav-links {
    display: flex;
    align-items: center;
    gap: 36px;
  }

  .mm-nav-links a {
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--ink-3);
    text-decoration: none;
    transition: color 0.18s;
  }

  .mm-nav-links a:hover,
  .mm-nav-links a.active { color: var(--ink); }

  .mm-nav-cta {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--amber) !important;
    border: 0.5px solid var(--amber);
    padding: 7px 16px;
    border-radius: 2px;
    transition: background 0.18s, color 0.18s !important;
  }

  .mm-nav-cta:hover {
    background: var(--amber) !important;
    color: var(--white) !important;
  }

  /* ── HERO ─────────────────────────────────────────────────────────────────── */
  .mm-hero {
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: 0;
    min-height: calc(100vh - 60px);
    border-bottom: 0.5px solid var(--rule);
  }

  .mm-hero-left {
    padding: 80px 64px 80px 48px;
    border-right: 0.5px solid var(--rule);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .mm-hero-eyebrow {
    font-size: 11px;
    font-weight: 400;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--amber);
    margin-bottom: 32px;
  }

  .mm-hero-intro {
    font-family: var(--serif);
    font-size: clamp(40px, 5vw, 64px);
    font-weight: 400;
    line-height: 1.1;
    color: var(--ink);
    margin-bottom: 32px;
  }

  .mm-hero-intro em {
    font-style: italic;
    color: var(--amber);
  }

  .mm-hero-pronunciation {
    font-family: var(--sans);
    font-size: clamp(14px, 1.4vw, 20px);
    font-weight: 300;
    font-style: normal;
    color: var(--ink-3);
    letter-spacing: 0.02em;
    vertical-align: middle;
    margin-left: 6px;
  }

  .mm-hero-bio {
    font-size: 15px;
    font-weight: 300;
    line-height: 1.8;
    color: var(--ink-2);
    max-width: 540px;
    margin-bottom: 48px;
  }

  .mm-hero-bottom {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  .mm-scroll-hint {
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-3);
    animation: nudgeDown 2.8s ease-in-out infinite;
  }

  @keyframes nudgeDown {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(4px); }
  }

  /* ── HERO SIDEBAR ─────────────────────────────────────────────────────────── */
  .mm-hero-right {
    padding: 80px 40px;
    background: var(--sand);
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  .mm-sidebar-label {
    font-size: 10px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--ink-3);
    font-weight: 400;
    margin-bottom: 14px;
  }

  .mm-stat {
    border-top: 0.5px solid var(--rule-md);
    padding-top: 16px;
  }

  .mm-stat-number {
    font-family: var(--serif);
    font-size: 42px;
    font-weight: 400;
    color: var(--ink);
    line-height: 1;
    margin-bottom: 4px;
  }

  .mm-stat-desc {
    font-size: 12px;
    font-weight: 300;
    color: var(--ink-3);
    line-height: 1.5;
  }

  .mm-skills-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .mm-skill-tag {
    font-size: 11px;
    font-weight: 400;
    letter-spacing: 0.04em;
    color: var(--ink-2);
    border: 0.5px solid var(--rule-md);
    border-radius: 2px;
    padding: 5px 11px;
    background: var(--white);
    transition: border-color 0.18s, color 0.18s;
  }

  .mm-skill-tag:hover {
    border-color: rgba(184,114,26,0.4);
    color: var(--amber);
  }

  /* ── SECTION HEADER ──────────────────────────────────────────────────────── */
  .mm-section-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 40px 48px 28px;
    border-bottom: 0.5px solid var(--rule);
  }

  .mm-section-label-lg {
    font-family: var(--serif);
    font-size: 22px;
    font-weight: 400;
    color: var(--ink);
  }

  .mm-section-meta {
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-3);
  }

  /* ── PROJECT CARDS ───────────────────────────────────────────────────────── */
  .mm-card {
    display: grid;
    grid-template-columns: 1fr 220px;
    gap: 0;
    border-bottom: 0.5px solid var(--rule);
    text-decoration: none;
    color: inherit;
    transition: background 0.22s;
  }

  .mm-card:hover { background: var(--sand); }

  .mm-card:hover .mm-card-arrow {
    opacity: 1;
    transform: translate(0, 0);
    color: var(--amber);
  }

  .mm-card-body {
    padding: 52px 48px;
    border-right: 0.5px solid var(--rule);
    display: grid;
    grid-template-columns: 1fr 100px;
    grid-template-rows: auto auto;
    column-gap: 32px;
    row-gap: 12px;
  }

  .mm-card-type {
    font-size: 10px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--ink-3);
    grid-column: 1;
    grid-row: 1;
  }

  .mm-card-title {
    font-family: var(--serif);
    font-size: 40px;
    font-weight: 400;
    color: var(--ink);
    line-height: 1;
    grid-column: 1;
    grid-row: 2;
    align-self: start;
  }

  .mm-card-body-icon {
    grid-column: 2;
    grid-row: 1 / 3;
    width: 88px;
    height: 88px;
    border-radius: 18px;
    object-fit: contain;
    align-self: center;
    justify-self: center;
    transition: transform 0.28s cubic-bezier(0.22,1,0.36,1);
  }

  .mm-card:hover .mm-card-body-icon {
    transform: scale(1.06) rotate(-3deg);
  }

  .mm-card-desc {
    font-size: 14px;
    font-weight: 300;
    color: var(--ink-2);
    line-height: 1.75;
    grid-column: 1 / 3;
    grid-row: 3;
    padding-top: 4px;
  }

  .mm-card-meta {
    padding: 48px 36px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-start;
    background: var(--sand);
    transition: background 0.22s;
  }

  .mm-card:hover .mm-card-meta { background: #EFF0E8; }

  .mm-card-meta-bottom {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .mm-card-tags {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  .mm-tag {
    font-size: 11px;
    padding: 4px 10px;
    border-radius: 2px;
    border: 0.5px solid var(--rule-md);
    color: var(--ink-3);
    letter-spacing: 0.04em;
    background: var(--white);
    white-space: nowrap;
    display: inline-block;
    width: fit-content;
    transition: border-color 0.18s, color 0.18s;
  }

  .mm-card:hover .mm-tag {
    border-color: rgba(184,114,26,0.25);
    color: var(--amber);
  }

  .mm-card-arrow {
    font-size: 20px;
    color: var(--ink-3);
    opacity: 0.3;
    transform: translate(-4px, 4px);
    transition: all 0.22s;
    line-height: 1;
    align-self: flex-end;
  }

  /* ── BEYOND WORK ─────────────────────────────────────────────────────────── */
  .mm-beyond {
    border-top: 0.5px solid var(--rule);
    border-bottom: 0.5px solid var(--rule);
  }

  .mm-beyond-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 40px 48px 28px;
    border-bottom: 0.5px solid var(--rule);
  }

  .mm-beyond-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
  }

  .mm-beyond-item {
    padding: 32px 40px;
    border-right: 0.5px solid var(--rule);
    border-bottom: 0.5px solid var(--rule);
  }

  .mm-beyond-item:nth-child(3n) { border-right: none; }
  .mm-beyond-item:nth-last-child(-n+3) { border-bottom: none; }

  .mm-beyond-emoji {
    font-size: 24px;
    margin-bottom: 14px;
    display: block;
  }

  .mm-beyond-title {
    font-family: var(--serif);
    font-size: 18px;
    font-weight: 400;
    color: var(--ink);
    margin-bottom: 8px;
  }

  .mm-beyond-desc {
    font-size: 13px;
    font-weight: 300;
    color: var(--ink-3);
    line-height: 1.65;
  }

  /* ── CONTACT ─────────────────────────────────────────────────────────────── */
  .mm-contact-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    border-top: 0.5px solid var(--rule);
  }

  .mm-contact-left {
    padding: 64px 48px;
    border-right: 0.5px solid var(--rule);
  }

  .mm-contact-headline {
    font-family: var(--serif);
    font-size: 48px;
    font-weight: 400;
    line-height: 1.1;
    color: var(--ink);
    margin-bottom: 20px;
  }

  .mm-contact-headline em { font-style: italic; color: var(--amber); }

  .mm-contact-sub {
    font-size: 14px;
    font-weight: 300;
    color: var(--ink-2);
    line-height: 1.7;
    margin-bottom: 36px;
    max-width: 360px;
  }

  .mm-contact-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--white);
    background: var(--amber);
    border: none;
    border-radius: 2px;
    padding: 14px 28px;
    text-decoration: none;
    cursor: pointer;
    transition: opacity 0.18s, transform 0.18s;
  }

  .mm-contact-btn:hover { opacity: 0.88; transform: translateY(-1px); }

  .mm-contact-right {
    padding: 64px 48px;
    background: var(--sand);
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 24px;
  }

  .mm-contact-link {
    display: flex;
    flex-direction: column;
    text-decoration: none;
    color: var(--ink);
    font-size: 14px;
    font-weight: 300;
    padding-bottom: 20px;
    border-bottom: 0.5px solid var(--rule);
    transition: color 0.18s, padding-left 0.2s;
  }

  .mm-contact-link:last-child { border-bottom: none; }
  .mm-contact-link:hover { color: var(--amber); padding-left: 6px; }

  .mm-contact-link-label {
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--ink-3);
    display: block;
    margin-bottom: 4px;
  }

  /* ── FOOTER ──────────────────────────────────────────────────────────────── */
  .mm-footer {
    border-top: 0.5px solid var(--rule);
    padding: 24px 48px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .mm-footer-name {
    font-family: var(--serif);
    font-size: 14px;
    font-weight: 400;
    color: var(--ink-3);
  }

  .mm-footer-copy {
    font-size: 11px;
    color: var(--ink-3);
    letter-spacing: 0.06em;
  }

  /* ── RESPONSIVE ──────────────────────────────────────────────────────────── */
  @media (max-width: 900px) {
    .mm-nav { padding: 0 24px; }
    .mm-hero { grid-template-columns: 1fr; min-height: auto; }
    .mm-hero-left { padding: 56px 24px 48px; }
    .mm-hero-right { padding: 40px 24px; border-top: 0.5px solid var(--rule); }
    .mm-hero-intro { font-size: 40px; }

    .mm-card { grid-template-columns: 1fr; }
    .mm-card-body { padding: 36px 24px; border-right: none; border-bottom: 0.5px solid var(--rule); grid-template-columns: 1fr 72px; }
    .mm-card-body-icon { width: 72px; height: 72px; }
    .mm-card-meta { padding: 24px; flex-direction: row; align-items: center; justify-content: space-between; }
    .mm-card-tags { flex-direction: row; flex-wrap: wrap; }
    .mm-card-meta-bottom { width: auto; flex-direction: row; align-items: center; gap: 12px; }

    .mm-section-header, .mm-beyond-header { padding: 32px 24px 20px; }

    .mm-beyond-grid { grid-template-columns: 1fr; }
    .mm-beyond-item { border-right: none; border-bottom: 0.5px solid var(--rule); padding: 28px 24px; }
    .mm-beyond-item:last-child { border-bottom: none; }

    .mm-contact-section { grid-template-columns: 1fr; }
    .mm-contact-left { padding: 48px 24px; border-right: none; border-bottom: 0.5px solid var(--rule); }
    .mm-contact-right { padding: 40px 24px; }

    .mm-footer { padding: 20px 24px; flex-direction: column; gap: 8px; text-align: center; }
  }
`;

/* ─── SCROLL TO TOP ON NAVIGATION ───────────────────────────────────────── */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    ReactGA.send({ hitType: 'pageview', page: pathname });
  }, [pathname]);
  return null;
}

/* ─── HEADER ─────────────────────────────────────────────────────────────── */
function Header() {
  const location = useLocation();
  const isActive = (path) => location.pathname === path ? 'active' : '';
  return (
    <nav className="mm-nav">
      <Link to="/" className="mm-nav-logo">
        <img src={Avatar} alt="Marques" className="mm-nav-avatar" />
        Marques Manta
      </Link>
      <div className="mm-nav-links">
        <a href="/#work" className={isActive('/')}>Work</a>
        <a href="mailto:marquesmanta@gmail.com" className="mm-nav-cta">Get in touch</a>
      </div>
    </nav>
  );
}

/* ─── PROJECTS ───────────────────────────────────────────────────────────── */
const projects = [
  {
    to: '/PorchpairPage',
    icon: PorchpairIcon,
    type: 'Founder · Operations',
    title: 'Porchpair',
    desc: 'Package theft costs Americans $8B+ a year. I built a neighborhood service connecting Baltimore residents with local businesses as secure drop-off points — validating the idea door-to-door, running the operations end-to-end, and growing it to 500+ deliveries.',
    tags: ['Operations', 'Growth', 'Service Design'],
  },
  {
    to: '/TrekkoraPage',
    icon: TrekkoraIcon,
    type: 'Product Management · Hackathon',
    title: 'Trekkora',
    desc: 'In a two-week hackathon, I acted as PM for an LLM-powered travel planning startup — running user interviews, defining the MVP scope from research findings, and managing the timeline to hit a working prototype.',
    tags: ['Product Strategy', 'User Research', 'LLM'],
  },
  {
    to: '/SeoulJuicePage',
    icon: SeoulJuiceIcon,
    type: 'Ecommerce · Project Management',
    title: 'Seoul Juice',
    desc: 'A D2C beverage brand with strong retail traction but lagging online sales. I took over the ecommerce operation — redesigned the site on Shopify, ran A/B tests, managed Amazon listings, and drove an 83% increase in online sales.',
    tags: ['Ecommerce', 'A/B Testing', 'Metrics Tracking'],
  },
  {
    to: '/LyftPage',
    icon: LyftIcon,
    type: 'Product Management · Case Study',
    title: 'Lyft Pricing',
    desc: 'Given a pricing problem for a new Lyft market. I modeled churn, CLV, and match rate sensitivity to find the take rate that maximized 12-month net revenue.',
    tags: ['Product Analytics', 'Pricing Strategy', 'Modeling'],
  },
];

/* ─── BEYOND WORK ────────────────────────────────────────────────────────── */
const beyondItems = [
  { emoji: '🏐', title: 'Current hobby',     desc: 'Beach volleyball.' },
  { emoji: '🏃', title: 'Goal for the year', desc: 'Run my first half marathon!' },
  { emoji: '📖', title: 'Currently reading', desc: 'Project Hail Mary.' },
  { emoji: '⚽', title: 'College soccer',    desc: 'Played winger at Case Western Reserve University.' },
  { emoji: '🌵', title: 'Grew up in Arizona', desc: 'I prefer the heat!' },
  { emoji: '🇺🇾', title: 'Part Uruguayan',   desc: 'Cant wait for the World Cup this summer'},
];

/* ─── HOME ───────────────────────────────────────────────────────────────── */
function Home() {
  return (
    <div>
      <section className="mm-hero">
        <div className="mm-hero-left">
          <div>
            <p className="mm-hero-eyebrow anim-fade-in delay-1">Product Specialist · Operations</p>
            <h1 className="mm-hero-intro anim-fade-up delay-2">
              Hi, I'm <em>Marques</em>
              <span className="mm-hero-pronunciation">(MARK-us)</span>
            </h1>
            <p className="mm-hero-bio anim-fade-up delay-3">
              I'm a product generalist with 5+ years at an early-stage medical device startup.
              I love being close to the product — talking to users, seeing how they interact
              with something, and finding ways to make it better. But I'm equally energized
              by the work that happens in the background — keeping projects on track, aligning
              team members, and making sure nothing falls through the cracks on the way to
              creating successful products and processes.
            </p>
          </div>
          <div className="mm-hero-bottom anim-fade-in delay-4">
            <span className="mm-scroll-hint">↓ Past work</span>
          </div>
        </div>

        <div className="mm-hero-right">
          <div className="anim-slide-right delay-2">
            <p className="mm-sidebar-label">Highlights</p>
            <div className="mm-stat">
              <div className="mm-stat-number">5+</div>
              <div className="mm-stat-desc">Years of startup product<br />and operations experience</div>
            </div>
          </div>
          <div className="anim-slide-right delay-3">
            <div className="mm-stat">
              <div className="mm-stat-number">43%</div>
              <div className="mm-stat-desc">Reduction in critical<br />user errors (ML software)</div>
            </div>
          </div>
          <div className="anim-slide-right delay-4">
            <div className="mm-stat">
              <div className="mm-stat-number">50%</div>
              <div className="mm-stat-desc">Faster decision-making via<br />agile delivery cycles</div>
            </div>
          </div>
          <div className="anim-slide-right delay-5">
            <p className="mm-sidebar-label">Core skills</p>
            <div className="mm-skills-list">
              {['Roadmapping', 'Agile', 'User Testing', 'Stakeholder Mgmt', 'A/B Testing', 'Figma', 'Jira', 'Metrics Tracking', 'Cross-Functional Coord.', 'User Story Mapping'].map(s => (
                <span key={s} className="mm-skill-tag">{s}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="mm-section-header" id="work">
        <span className="mm-section-label-lg">Past work</span>
        <span className="mm-section-meta">4 projects</span>
      </div>

      <div>
        {projects.map((p) => (
          <Link key={p.to} to={p.to} className="mm-card">
            <div className="mm-card-body">
              <div className="mm-card-type">{p.type}</div>
              <div className="mm-card-title">{p.title}</div>
              <img src={p.icon} alt={p.title} className="mm-card-body-icon" />
              <p className="mm-card-desc">{p.desc}</p>
            </div>
            <div className="mm-card-meta">
              <div className="mm-card-meta-bottom">
                <div className="mm-card-tags">
                  {p.tags.map(t => <span key={t} className="mm-tag">{t}</span>)}
                </div>
                <span className="mm-card-arrow">↗</span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mm-beyond">
        <div className="mm-beyond-header">
          <span className="mm-section-label-lg">Beyond work</span>
          <span className="mm-section-meta">a bit about me</span>
        </div>
        <div className="mm-beyond-grid">
          {beyondItems.map((item) => (
            <div key={item.title} className="mm-beyond-item">
              <span className="mm-beyond-emoji">{item.emoji}</span>
              <div className="mm-beyond-title">{item.title}</div>
              <p className="mm-beyond-desc">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mm-contact-section">
        <div className="mm-contact-left">
          <h2 className="mm-contact-headline">Let's<br /><em>Connect</em></h2>
          <p className="mm-contact-sub">
            I bring structure to ambiguous problems across product operations,
            management, and cross-functional teams.
          </p>
          <a href="mailto:marquesmanta@gmail.com" className="mm-contact-btn">Send an email ↗</a>
        </div>
        <div className="mm-contact-right">
          <a href="mailto:marquesmanta@gmail.com" className="mm-contact-link">
            <span className="mm-contact-link-label">Email</span>
            marquesmanta@gmail.com
          </a>
          <a href="https://linkedin.com/in/marques-manta" target="_blank" rel="noreferrer" className="mm-contact-link">
            <span className="mm-contact-link-label">LinkedIn</span>
            linkedin.com/in/marques-manta
          </a>
        </div>
      </div>

      <footer className="mm-footer">
        <span className="mm-footer-name">Marques Manta</span>
      </footer>
    </div>
  );
}

function Contact() {
  useEffect(() => { window.location.href = 'mailto:marquesmanta@gmail.com'; }, []);
  return <div style={{ padding: '80px 48px', fontFamily: 'var(--sans)' }}>Opening your mail client…</div>;
}

function App() {
  return (
    <Router>
      <style>{styles}</style>
      <ScrollToTop />
      <Header />
      <Routes>
        <Route path="/"               element={<Home />} />
        <Route path="/resume"         element={<ResumePage />} />
        <Route path="/contact"        element={<Contact />} />
        <Route path="/PorchpairPage"  element={<PorchpairPage />} />
        <Route path="/TrekkoraPage"   element={<TrekkoraPage />} />
        <Route path="/SeoulJuicePage" element={<SeoulJuicePage />} />
        <Route path="/LyftPage"       element={<LyftPage />} />
      </Routes>
    </Router>
  );
}

export default App;
